import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/formatters';
import './CreditCardsModal.css';

const CreditCardsModal = ({ onClose, onSelectCardForTransaction }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCard, setEditingCard] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        bank_name: 'Itaú',
        last_four: '',
        card_holder: '',
        total_limit: '',
        closing_day: '5',
        due_day: '12',
        color_theme: '#000080'
    });

    const fetchCards = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/financas/cartoes', { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setCards(data);
            }
        } catch (error) {
            console.error('Erro ao buscar cartões:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleOpenCreate = () => {
        setEditingCard(null);
        setFormData({
            name: '',
            bank_name: 'Itaú',
            last_four: '',
            card_holder: '',
            total_limit: '',
            closing_day: '5',
            due_day: '12',
            color_theme: '#000080'
        });
        setShowForm(true);
    };

    const handleOpenEdit = (card) => {
        setEditingCard(card);
        setFormData({
            name: card.name,
            bank_name: card.bank_name,
            last_four: card.last_four || '',
            card_holder: card.card_holder || '',
            total_limit: card.total_limit,
            closing_day: card.closing_day,
            due_day: card.due_day,
            color_theme: card.color_theme || '#000080'
        });
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const url = editingCard ? `/api/financas/cartoes/${editingCard.id}` : '/api/financas/cartoes';
        const method = editingCard ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                alert(data.message || 'Cartão salvo com sucesso!');
                setShowForm(false);
                fetchCards();
            } else {
                alert(data.message || 'Erro ao salvar cartão.');
            }
        } catch (error) {
            console.error('Erro ao salvar cartão:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Deseja realmente desativar este cartão?')) return;
        try {
            const res = await fetch(`/api/financas/cartoes/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (res.ok) {
                alert('Cartão removido!');
                fetchCards();
            }
        } catch (error) {
            console.error('Erro ao remover cartão:', error);
        }
    };

    const [selectedCardStatement, setSelectedCardStatement] = useState(null);
    const [showStatementModal, setShowStatementModal] = useState(false);

    const handleOpenStatement = async (card) => {
        try {
            const res = await fetch(`/api/financas/cartoes/${card.id}/extrato`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setSelectedCardStatement(data);
                setShowStatementModal(true);
            }
        } catch (error) {
            console.error('Erro ao carregar extrato do cartão:', error);
        }
    };

    const handlePayBill = async (card) => {
        if (!window.confirm(`Deseja dar baixa na fatura do cartão "${card.name}"?\n\nIsso marcará todos os lançamentos pendentes deste cartão como Concluídos e liberará o limite.`)) return;

        try {
            const res = await fetch(`/api/financas/cartoes/${card.id}/quitar`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await res.json();
            if (res.ok) {
                alert(data.message);
                fetchCards();
                if (showStatementModal && selectedCardStatement?.card?.id === card.id) {
                    handleOpenStatement(card);
                }
            }
        } catch (error) {
            console.error('Erro ao quitar fatura:', error);
        }
    };

    // Totais Consolidados
    const totalLimitSum = cards.reduce((acc, c) => acc + Number(c.total_limit || 0), 0);
    const totalUsedSum = cards.reduce((acc, c) => acc + Number(c.used_limit || 0), 0);
    const totalAvailableSum = Math.max(0, totalLimitSum - totalUsedSum);

    return (
        <div className="cards-modal-overlay">
            <div className="cards-modal win-style">
                <div className="win-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>💳 Gestão de Cartões de Crédito ({cards.length} cadastrados)</span>
                    <button className="btn-close-modal" onClick={onClose}>✕</button>
                </div>

                <div className="win-body">
                    {/* Dashboard de Totais Simplificados */}
                    <div className="cards-summary-bar">
                        <div className="summary-box box-limit">
                            <label>🏦 Limite Total Somado</label>
                            <span className="val">R$ {formatCurrency(totalLimitSum)}</span>
                        </div>
                        <div className="summary-box box-used">
                            <label>🔴 Faturas / Gasto Usado</label>
                            <span className="val">R$ {formatCurrency(totalUsedSum)}</span>
                        </div>
                        <div className="summary-box box-available">
                            <label>🟢 Limite Livre Disponível</label>
                            <span className="val">R$ {formatCurrency(totalAvailableSum)}</span>
                        </div>
                    </div>

                    <div className="cards-toolbar">
                        <button className="btn-classic primary" onClick={handleOpenCreate}>
                            ➕ Cadastrar Novo Cartão
                        </button>
                        <span className="toolbar-hint">Clique em um cartão para ver opções de quitação de fatura ou gastos.</span>
                    </div>

                    {showForm && (
                        <form onSubmit={handleSubmit} className="card-form-box win-style">
                            <h4>{editingCard ? '✏️ Editar Cartão' : '➕ Novo Cartão de Crédito'}</h4>
                            <div className="form-grid-cards">
                                <div className="field">
                                    <label>Nome / Apelido do Cartão *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Ex: Itaú Master do Pai"
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label>Banco emissor *</label>
                                    <select name="bank_name" value={formData.bank_name} onChange={handleInputChange}>
                                        <option value="Itaú">Itaú</option>
                                        <option value="Bradesco">Bradesco</option>
                                        <option value="Santander">Santander</option>
                                        <option value="Nubank">Nubank</option>
                                        <option value="Caixa">Caixa Econômica</option>
                                        <option value="Banco do Brasil">Banco do Brasil</option>
                                        <option value="C6 Bank">C6 Bank</option>
                                        <option value="Inter">Banco Inter</option>
                                        <option value="Porto Seguro">Porto Seguro</option>
                                        <option value="Outro">Outro Banco</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label>Últimos 4 dígitos</label>
                                    <input
                                        type="text"
                                        name="last_four"
                                        value={formData.last_four}
                                        onChange={handleInputChange}
                                        placeholder="Ex: 4321"
                                        maxLength="4"
                                    />
                                </div>
                                <div className="field">
                                    <label>Nome do Titular</label>
                                    <input
                                        type="text"
                                        name="card_holder"
                                        value={formData.card_holder}
                                        onChange={handleInputChange}
                                        placeholder="Ex: RA Multi Services"
                                    />
                                </div>
                                <div className="field">
                                    <label>Limite Total de Crédito (R$) *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="total_limit"
                                        value={formData.total_limit}
                                        onChange={handleInputChange}
                                        placeholder="Ex: 10000"
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label>Dia Fechamento Fatura</label>
                                    <input
                                        type="number"
                                        name="closing_day"
                                        value={formData.closing_day}
                                        onChange={handleInputChange}
                                        min="1"
                                        max="31"
                                    />
                                </div>
                                <div className="field">
                                    <label>Dia Vencimento Fatura</label>
                                    <input
                                        type="number"
                                        name="due_day"
                                        value={formData.due_day}
                                        onChange={handleInputChange}
                                        min="1"
                                        max="31"
                                    />
                                </div>
                                <div className="field">
                                    <label>Cor do Cartão</label>
                                    <input
                                        type="color"
                                        name="color_theme"
                                        value={formData.color_theme}
                                        onChange={handleInputChange}
                                        style={{ height: '35px', cursor: 'pointer' }}
                                    />
                                </div>
                            </div>
                            <div className="form-actions" style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button type="button" className="btn-classic" onClick={() => setShowForm(false)}>Cancelar</button>
                                <button type="submit" className="btn-classic primary">{editingCard ? 'Salvar Alterações' : 'Cadastrar Cartão'}</button>
                            </div>
                        </form>
                    )}

                    {loading ? (
                        <p style={{ textAlign: 'center', padding: '20px' }}>Carregando cartões...</p>
                    ) : (
                        <div className="cards-grid">
                            {cards.length > 0 ? cards.map(card => {
                                const percentUsed = card.total_limit > 0 ? Math.min(100, (card.used_limit / card.total_limit) * 100) : 0;
                                const barColor = percentUsed > 80 ? '#e74c3c' : percentUsed > 50 ? '#f39c12' : '#2ecc71';

                                return (
                                    <div key={card.id} className="credit-card-item" style={{ borderTop: `6px solid ${card.color_theme || '#000080'}` }}>
                                        <div className="card-top">
                                            <span className="card-bank">{card.bank_name}</span>
                                            <span className="card-digits">{card.last_four ? `•••• ${card.last_four}` : ''}</span>
                                        </div>
                                        <h3 className="card-title">{card.name}</h3>
                                        <p className="card-holder">{card.card_holder || 'Empresa'}</p>

                                        <div className="card-limit-info">
                                            <div className="limit-row">
                                                <span>Fatura Aberta (Usado):</span>
                                                <strong style={{ color: '#c0392b' }}>R$ {formatCurrency(card.used_limit)}</strong>
                                            </div>
                                            <div className="limit-row">
                                                <span>Limite Disponível:</span>
                                                <strong style={{ color: '#27ae60' }}>R$ {formatCurrency(card.available_limit)}</strong>
                                            </div>
                                            <div className="limit-row sub">
                                                <span>Limite Total: R$ {formatCurrency(card.total_limit)}</span>
                                                <span>Fech: dia {card.closing_day} | Venc: dia {card.due_day}</span>
                                            </div>
                                        </div>

                                        {/* Barra Visual de Uso do Limite */}
                                        <div className="progress-bar-container" title={`${percentUsed.toFixed(1)}% do limite ocupado`}>
                                            <div className="progress-bar-fill" style={{ width: `${percentUsed}%`, backgroundColor: barColor }}></div>
                                        </div>

                                        <div className="card-actions">
                                            <button
                                                className="btn-classic primary btn-sm"
                                                onClick={() => handlePayBill(card)}
                                                title="Marca todas as contas deste cartão como pagas e restaura o limite"
                                            >
                                                ✅ Quitar Fatura (Liberar Limite)
                                            </button>
                                            <div className="card-sub-actions">
                                                <button className="btn-classic btn-sm" onClick={() => handleOpenStatement(card)}>📋 Gastos & Histórico</button>
                                                <button className="btn-classic btn-sm" onClick={() => handleOpenEdit(card)}>✏️ Editar</button>
                                                <button className="btn-classic btn-sm danger" onClick={() => handleDelete(card.id)}>🗑️ Excluir</button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }) : (
                                <p style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '30px', color: '#666' }}>
                                    Nenhum cartão de crédito cadastrado ainda. Clicar no botão "+ Cadastrar Novo Cartão" acima para adicionar o primeiro!
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Extrato e Histórico de Gastos do Cartão */}
            {showStatementModal && selectedCardStatement && (
                <div className="cards-modal-overlay" style={{ zIndex: 2200 }}>
                    <div className="cards-modal win-style" style={{ maxWidth: '850px', width: '90%' }}>
                        <div className="win-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>📋 Extrato & Histórico: {selectedCardStatement.card.name} ({selectedCardStatement.card.bank_name})</span>
                            <button className="btn-close-modal" onClick={() => setShowStatementModal(false)}>✕</button>
                        </div>

                        <div className="win-body">
                            <div className="card-statement-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', padding: '15px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #ccc' }}>
                                <div>
                                    <h3 style={{ margin: '0 0 5px 0', color: '#000080' }}>{selectedCardStatement.card.name}</h3>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>
                                        Fechamento: Dia <strong>{selectedCardStatement.card.closing_day}</strong> | Vencimento: Dia <strong>{selectedCardStatement.card.due_day}</strong>
                                    </p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <button className="btn-classic primary" onClick={() => handlePayBill(selectedCardStatement.card)}>
                                        ✅ Quitar Fatura Atual
                                    </button>
                                </div>
                            </div>

                            <h4 style={{ margin: '15px 0 10px 0', color: '#333' }}>Lançamentos Vinculados a este Cartão ({selectedCardStatement.transactions.length})</h4>

                            {selectedCardStatement.transactions.length > 0 ? (
                                <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ background: '#e0e0e0' }}>
                                            <th>Vencimento</th>
                                            <th>Descrição</th>
                                            <th>Pessoa/Empresa</th>
                                            <th>Parcela</th>
                                            <th>Valor</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedCardStatement.transactions.map(t => (
                                            <tr key={t.ID} style={{ background: t.Status === 'Concluído' ? '#f4fbf4' : '#fff9f0' }}>
                                                <td>{t.DataVencimento?.substring(0, 10).split('-').reverse().join('/')}</td>
                                                <td><strong>{t.Descricao}</strong></td>
                                                <td>{t.Entidade || '---'}</td>
                                                <td>{t.TotalParcelas > 1 ? `${t.ParcelaAtual}/${t.TotalParcelas}` : '1/1'}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 'bold', color: t.Tipo === 'Pagar' ? '#8b0000' : '#006400' }}>
                                                    R$ {formatCurrency(t.Valor)}
                                                </td>
                                                <td>
                                                    <span className={`status-pill ${t.Status === 'Concluído' ? 'concluido' : 'pendente'}`}>
                                                        {t.Status === 'Concluído' ? '✓ Pago/Baixado' : '⏳ Fatura Aberta'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                    Nenhum lançamento registrado neste cartão ainda.
                                </p>
                            )}

                            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                <button className="btn-classic" onClick={() => setShowStatementModal(false)}>Fechar Extrato</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreditCardsModal;
